import { pairwise } from "./utils";

export type Discipline = "hiking" | "winter hiking" | "snowshoe hiking";
export type PhysicalDemand = "low" | "medium" | "high";

const CS: number[] = [
  14.271, 3.6992, 2.5922, -1.4384, 0.32105, 0.81542, -0.090261, -0.20757, 0.010192, 0.028588, -0.00057466, -0.0021842,
  0.000015176, 0.000086894, -0.00000013584, -0.0000014026,
];

const WALKING_TIME_DISCIPLINE_FACTOR: { [key in Discipline]: number } = {
  hiking: 1,
  "winter hiking": 1.25,
  "snowshoe hiking": 1.5,
};

// walking time in minutes
function computeWalkingTime(length: number, height: number): number {
  if (length <= 0) {
    return 0;
  }
  const slope10 = Math.min(Math.max((10 * height) / length, -4), 4);
  let minutesPerKm = 0;

  for (let i = 0; i < CS.length; i++) {
    minutesPerKm += CS[i] * slope10 ** i;
  }
  return (length * minutesPerKm) / 1000;
}

export function distance(coord1: number[], coord2: number[]): number {
  const dx = coord1[0] - coord2[0];
  const dy = coord1[1] - coord2[1];
  return Math.sqrt(dx * dx + dy * dy);
}

export function walkingTime(coordinates: number[][], discipline: Discipline): number {
  let duration = 0;
  for (const [previous, current] of pairwise(coordinates)) {
    duration += computeWalkingTime(current[3] - previous[3], current[2] - previous[2]);
  }
  return duration * WALKING_TIME_DISCIPLINE_FACTOR[discipline];
}

export function walkingAscentDescent(coordinates: number[][]): { ascent: number; descent: number } {
  let ascent = 0;
  let descent = 0;
  for (const [previous, current] of pairwise(coordinates)) {
    const zDiff = current[2] - previous[2];
    if (zDiff > 0) {
      ascent += zDiff;
    } else {
      descent += Math.abs(zDiff);
    }
  }

  return { ascent, descent };
}

export function walkingPhysicalDemand(
  distance: number,
  ascent: number,
  descent: number,
  discipline: Discipline,
): PhysicalDemand | undefined {
  const ascent10 = ascent * 10;
  const performanceKm = ascent10 + distance;
  const relevantHeightDifference = Math.max(ascent, descent / 2);
  let limitH1 = 300;
  let limitH2 = 800;
  let limitPk1 = 12000;
  let limitPk2 = 20000;

  if (discipline === "hiking") {
    limitH1 = 300;
    limitH2 = 800;
    limitPk1 = 12000;
    limitPk2 = 20000;
  } else if (discipline === "winter hiking") {
    limitH1 = 200;
    limitH2 = 400;
    limitPk1 = 8000;
    limitPk2 = 13000;
  } else if (discipline === "snowshoe hiking") {
    limitH1 = 200;
    limitH2 = 500;
    limitPk1 = 6000;
    limitPk2 = 11000;
  } else {
    console.error(`Invalid discipline: ${discipline}`);
    return;
  }

  if (relevantHeightDifference <= limitH1 && performanceKm <= limitPk1 && ascent10 <= distance) {
    return "low";
  }
  if (relevantHeightDifference <= limitH2 && performanceKm <= limitPk2 && ascent10 <= distance) {
    return "medium";
  }
  return "high";
}
